import { useEffect, useRef, useState } from "react";
import { Alert, Button, Group, Modal, Radio, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { FiInfo, FiSave } from "react-icons/fi";
import type { IViewPresetScope } from "@multiconsult-group/multiplacer-core";
import { usePostMultiplacerViewsMutation } from "../../../openapi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setSelectedViewPresetInRedux, setViewPresetListCategoryInRedux } from "../../ViewerRedux/viewerSlice";
import { selectViewerView } from "../viewSelectors";
import { viewerViewFingerprint } from "../viewerViewFingerprint";
import { selectViewPresetSaveSnapshot } from "./viewSettingsSelectors";
import { ViewSettingsDialog } from "./ViewSettingsDialog";

type SaveSnapshot = NonNullable<ReturnType<typeof selectViewPresetSaveSnapshot>>;

export function SaveViewPresetButton() {
  const currentSnapshot = useAppSelector(selectViewPresetSaveSnapshot);
  const category = useAppSelector(state => state.viewer.viewPresetListCategory);
  const [draft, setDraft] = useState<{ snapshot: SaveSnapshot; scope: IViewPresetScope } | null>(null);
  const projectNo = currentSnapshot?.projectNo;
  useEffect(() => { setDraft(null); }, [projectNo]);

  return <>
    <Button leftSection={<FiSave />} disabled={!currentSnapshot} onClick={() => {
      if (currentSnapshot) setDraft({ snapshot: currentSnapshot, scope: category === "project" ? "project" : "personal" });
    }}>Lagre visning</Button>
    {draft && draft.snapshot.projectNo === projectNo && (
      <SaveViewPresetModal snapshot={draft.snapshot} initialScope={draft.scope} onClose={() => setDraft(null)} />
    )}
  </>;
}

function SaveViewPresetModal({ snapshot, initialScope, onClose }: {
  snapshot: SaveSnapshot; initialScope: IViewPresetScope; onClose: () => void;
}) {
  const dispatch = useAppDispatch();
  const [createPreset, { isLoading }] = usePostMultiplacerViewsMutation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scope, setScope] = useState(initialScope);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState("");
  const busy = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const save = async () => {
    if (busy.current) return;
    setError("");
    busy.current = true;
    try {
      const saved = await createPreset({ body: {
        projectNo: snapshot.projectNo,
        data: { kind: "preset", view: snapshot.view, name, description, scope },
      } }).unwrap();
      if (saved.kind !== "preset") throw new Error("Expected a preset response");
      if (!mounted.current) return;
      dispatch((_dispatch, getState) => {
        const state = getState();
        if (state.design.projectNo !== snapshot.projectNo) return;
        if (viewerViewFingerprint(selectViewerView(state)) !== viewerViewFingerprint(snapshot.view)) return;
        _dispatch(setSelectedViewPresetInRedux({ source: "user", id: saved._id }));
        _dispatch(setViewPresetListCategoryInRedux(saved.scope));
      });
      onClose();
    } catch {
      if (mounted.current) setError("Kunne ikke lagre visningen. Prøv igjen.");
    } finally {
      busy.current = false;
    }
  };

  return <>
    <Modal opened onClose={onClose} title="Lagre visning" centered size="md"
      closeOnClickOutside={!isLoading && !showSettings} closeOnEscape={!isLoading && !showSettings} withCloseButton={!isLoading}>
      <form onSubmit={event => { event.preventDefault(); void save(); }}>
        <Stack gap="md">
          <TextInput label="Tittel" placeholder="Gi visningen et navn" data-autofocus required maxLength={100}
            value={name} onChange={event => setName(event.currentTarget.value)} disabled={isLoading} />
          <Textarea label="Beskrivelse" placeholder="Hva viser denne visningen?" maxLength={500} minRows={3} autosize
            value={description} onChange={event => setDescription(event.currentTarget.value)} disabled={isLoading} />
          <Radio.Group label="Tilgjengelig for" value={scope} onChange={value => {
            if (value === "personal" || value === "project") setScope(value);
          }}>
            <Stack gap="xs" mt="xs">
              <Radio value="personal" label="Bare meg" disabled={isLoading} />
              <Radio value="project" label="Alle med tilgang til prosjektet" disabled={isLoading} />
            </Stack>
          </Radio.Group>
          <Text size="sm" c="dimmed">Lagrer filtre, farger og valideringsinnstillinger. Visningen kan brukes i alle moduler.</Text>
          <Button variant="neutral" leftSection={<FiInfo />} onClick={() => setShowSettings(true)}>Vis innstillinger</Button>
          {error && <Alert color="red" role="alert">{error}</Alert>}
          <Group justify="flex-end">
            <Button variant="neutral" disabled={isLoading} onClick={onClose}>Avbryt</Button>
            <Button type="submit" loading={isLoading}>Lagre</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
    {showSettings && <ViewSettingsDialog details={{ ...snapshot.details, name: name.trim() || "Gjeldende visning", description }}
      onClose={() => setShowSettings(false)} />}
  </>;
}
